import { type App, computed, inject, ref, type Ref } from 'vue'
import { CountryCode, type ShippingCountryFragment } from '@/graphql/api'
import { getRegionCodeForCountry } from '@/plugins/country2.ts'
import { getLocaleByCountry } from '@/helpers/country-locale.ts'
import {
    getIsActiveRegion,
    noPrefixRegion,
    type RegionPlugin,
} from '@/plugins/region.ts'
import type { AppTranslator } from '@/i18n'
import {
    createTransformedCookie,
    type ReactiveCookie,
    type ReactiveCookiePlugin,
} from '@/plugins/cookie-plugin.ts'

const pluginKey = 'app.plugin.country'

export function isCountryCode(value: unknown): value is CountryCode {
    return (
        typeof value === 'string' &&
        Object.values(CountryCode).includes(value as CountryCode)
    )
}

export type CountryPlugin = {
    countryCode: Ref<CountryCode>
    country: Readonly<Ref<ShippingCountryFragment['countries'][number] | null>>
    countries: Ref<ShippingCountryFragment[]>
    selectCountry: (countryCode: CountryCode) => Promise<void>
    countryCookie: ReactiveCookie<CountryCode | undefined>
}

export function installCountryPlugin(
    app: App,
    regionPlugin: RegionPlugin,
    setLocale: AppTranslator['setLocale'],
    cookies: ReactiveCookiePlugin
): CountryPlugin {
    const { createCookie } = cookies
    const countryCookie = createTransformedCookie<
        CountryCode | undefined,
        string | undefined
    >(createCookie('country'), {
        transform(value) {
            return value
        },
        reverseTransform(value) {
            if (typeof value === 'string' && isCountryCode(value)) {
                return value
            }

            return undefined
        },
    })

    const countryCode = ref<CountryCode>('GB')

    const countries = ref<ShippingCountryFragment[]>([])

    const country = computed(() => {
        for (const section of countries.value) {
            for (const country of section.countries) {
                if (country.isoCode === countryCode.value) {
                    return country
                }
            }
        }

        return null
    })

    const plugin: CountryPlugin = Object.freeze({
        countryCode,
        country,
        countries,
        selectCountry: async (_countryCode: CountryCode) => {
            countryCode.value = _countryCode
            countryCookie.value = _countryCode

            setLocale(getLocaleByCountry(_countryCode))

            const region = getRegionCodeForCountry(_countryCode)
            if (region && getIsActiveRegion(region)) {
                await regionPlugin.selectRegion(region)
            } else {
                await regionPlugin.selectRegion(noPrefixRegion)
            }
        },
        countryCookie,
    })

    app.provide(pluginKey, plugin)

    return plugin
}

export function useCountryPlugin(): CountryPlugin {
    return inject(pluginKey) as CountryPlugin
}
